/**
 * The one popover behaviour the shell needs three times: the workspace switcher, the
 * notification centre and the person menu.
 *
 * Written once rather than three times, because the parts that are easy to get wrong are
 * the parts that are easy to forget: `aria-expanded` on the trigger, Escape closing it,
 * a click outside closing it, and focus going back to the trigger when it closes.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

export interface PopoverState {
  readonly open: boolean;
  readonly anchorRef: RefObject<HTMLDivElement | null>;
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  readonly toggle: () => void;
  readonly close: () => void;
}

export function usePopover(): PopoverState {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const toggle = useCallback(() => {
    setOpen((current) => !current);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent): void => {
      const anchor = anchorRef.current;
      if (anchor !== null && event.target instanceof Node && !anchor.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    globalThis.document.addEventListener("mousedown", onPointerDown);
    globalThis.document.addEventListener("keydown", onKeyDown);
    return () => {
      globalThis.document.removeEventListener("mousedown", onPointerDown);
      globalThis.document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return { open, anchorRef, triggerRef, toggle, close };
}
